import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { getGlobalStyles } from '@tanium/coreui-mixins';
import { Box, BoxProps } from '@tanium/react-box';
import styled, { EmotionProvider } from '@tanium/react-emotion';
import { ThemeProvider } from '@tanium/react-theme-context';
import { Theme } from '@tanium/theme-data';

type ThemeWrapperProps<TElementType = 'div'> = BoxProps<TElementType> & {
  /**
   * The theme which will be applied to this wrapper and its children.
   */
  theme: Theme;
};

const ThemeStyles = styled(Box)(getGlobalStyles);

/**
 * Renders itself and its children with the provided theme.
 *
 * This includes all necessary context providers and styles for applying a
 * theme.
 *
 * This component composes `Box` so it can be rendered as another element in the
 * same way using the `as` prop.
 */
export const ThemeWrapper = React.memo(
  React.forwardRef<HTMLElement, ThemeWrapperProps>(({ theme, ...props }, ref) => (
    <EmotionProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <ThemeStyles {...props} ref={ref} />
      </ThemeProvider>
    </EmotionProvider>
  ))
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
) as <TElementType extends unknown = 'div'>(props: ThemeWrapperProps<TElementType>) => React.ReactElement;
